import { CopyOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import * as Component from 'components';
import { BASE_URL, buildShortsWorkOrderFeedUrl } from 'constants/api';
import PresetSelect from 'content/shorts-preset/components/PresetSelect';
import WorkOrderFeedModal from 'content/shorts-preset/components/WorkOrderFeedModal';
import {
  WORK_ORDER_MAX_ITEMS,
  WORK_ORDER_PLAYLIST_TYPE,
  WORK_ORDER_WINDOW_DAYS,
} from 'content/shorts-preset/constants';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { playlistApiService } from '../services/playlistApiService';
import { FeedValidationResult, PlaylistInterface } from '../types/playlistType';
import './PlaylistDetail.css';

const { Text, Link } = Typography;
const MRSS_URL_FORMAT = `${BASE_URL}/api/v1/playlist/{id}/mrss/{service_name}`;

/**
 * Ad cue point spacing, in seconds. Server bounds (`PlaylistSerializer`); anything outside
 * comes back as 400. Left empty the server falls back to DEFAULT — the value is null in the
 * database, not 720, so the placeholder shows the default rather than pre-filling it.
 */
const CUE_POINT_INTERVAL_MIN = 1;
const CUE_POINT_INTERVAL_MAX = 86400;
const CUE_POINT_INTERVAL_DEFAULT = 720;

/**
 * MRSS feed formats. The backend picks the format from the `{service_name}` path
 * segment (see PlaylistMrssView), so every playlist is reachable in every format
 * regardless of its `type`. An unknown name falls through to the default feed.
 */
const MRSS_SERVICES: { name: string; label: string }[] = [
  { name: 'zype', label: 'Zype' },
  { name: 'amagi', label: 'Amagi' },
  { name: 'xumo', label: 'Xumo' },
  { name: 'vimai', label: 'Vimai' },
  { name: 'odk', label: 'ODK' },
];

const buildMrssUrl = (playlistId: string, serviceName: string): string =>
  MRSS_URL_FORMAT.replace('{id}', playlistId).replace('{service_name}', serviceName);

export const PlaylistDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [playlistData, setPlaylistData] = useState<PlaylistInterface>();
  const navigate = useNavigate();

  // `type` is read-only here, but the preset field and the work-order section depend on it,
  // so read it from the form store rather than from the fetch response alone.
  const playlistType = Form.useWatch<string | undefined>('type', form);
  const selectedPreset = Form.useWatch<number | null | undefined>('preset', form);
  const isWorkOrderPlaylist = playlistType === WORK_ORDER_PLAYLIST_TYPE;
  const [feedModalOpen, setFeedModalOpen] = useState(false);

  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<FeedValidationResult | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [recoveringIds, setRecoveringIds] = useState<Set<number>>(new Set());
  const [recoverAllProgress, setRecoverAllProgress] = useState<string | null>(null);

  const fetchPlaylist = async (): Promise<void> => {
    const playlist = await playlistApiService.fetchById(Number(id));
    form.setFieldsValue(playlist);
    setPlaylistData(playlist);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPlaylist().then();
  }, [id, form]);

  const handleValidateFeed = async () => {
    setValidating(true);
    try {
      const result = await playlistApiService.validateFeed(Number(id));
      if (result.invalid_count === 0) {
        message.success(`All ${result.total_count} items valid`);
      } else {
        setValidationResult(result);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Feed validation failed:', error);
    } finally {
      setValidating(false);
    }
  };

  const handleRecover = async (videoId: number) => {
    setRecoveringIds(prev => new Set(prev).add(videoId));
    try {
      await playlistApiService.recoverVideo(videoId);
      message.success('Recovery initiated');
    } catch (error) {
      console.error('Recovery failed:', error);
    } finally {
      setRecoveringIds(prev => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
    }
  };

  const handleRecoverAll = async () => {
    if (!validationResult) return;
    const recoverableItems = validationResult.invalid_items.filter(item => item.video_id !== null);
    const total = recoverableItems.length;

    for (let i = 0; i < total; i++) {
      setRecoverAllProgress(`Recovering ${i + 1}/${total}...`);
      try {
        await playlistApiService.recoverVideo(recoverableItems[i].video_id!);
      } catch (error) {
        console.error(`Failed to recover: ${recoverableItems[i].title}`, error);
      }
    }
    setRecoverAllProgress('Complete');

    // Re-validate to refresh results
    try {
      const result = await playlistApiService.validateFeed(Number(id));
      if (result.invalid_count === 0) {
        setModalOpen(false);
        message.success(`All ${result.total_count} items valid`);
      } else {
        setValidationResult(result);
      }
    } catch (error) {
      console.error('Re-validation failed:', error);
    }
    setRecoverAllProgress(null);
  };

  const recoverableCount =
    validationResult?.invalid_items.filter(item => item.video_id !== null).length ?? 0;

  const workOrderFeedUrl = buildShortsWorkOrderFeedUrl(String(id));

  const handleCopyFeedUrl = async () => {
    try {
      await navigator.clipboard.writeText(workOrderFeedUrl);
      message.success('Feed URL copied');
    } catch {
      message.error('Could not copy — select the URL and copy manually');
    }
  };

  const handleUpdate = async () => {
    const newPlaylist = form.getFieldsValue();
    // Clearing the select yields undefined, which JSON.stringify drops — the link would
    // never be released. Only touch the key when the field is on screen at all.
    if (isWorkOrderPlaylist) {
      newPlaylist.preset = newPlaylist.preset ?? null;
    }
    // Same trap: emptying the number input yields undefined, so the key would vanish and the
    // playlist would keep its old interval. null is what puts it back on the 720 default.
    newPlaylist.cue_point_interval = newPlaylist.cue_point_interval ?? null;
    await playlistApiService.update(Number(id), newPlaylist);
    navigate('/playlists/');
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Content ID',
      dataIndex: 'content_id',
      key: 'content_id',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: FeedValidationResult['invalid_items'][number]) => {
        if (record.video_id !== null) {
          return (
            <Button
              type="link"
              loading={recoveringIds.has(record.video_id)}
              onClick={() => handleRecover(record.video_id!)}
            >
              Recover
            </Button>
          );
        }
        const path =
          record.content_type === 'movie'
            ? `/movie/edit/${record.content_id}`
            : `/episode/edit/${record.content_id}`;
        return (
          <Button type="link" onClick={() => window.open(path, '_blank')}>
            View Content
          </Button>
        );
      },
    },
  ];

  return (
    <>
      {isLoading ? (
        <Component.LoadingSpinnerComponent />
      ) : (
        <div>
          <div className="playlist-details-page">
            <Form
              form={form}
              layout="vertical"
              className="playlist-details-form"
              initialValues={playlistData}
            >
              <Form.Item name="title" label="Title">
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input />
              </Form.Item>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'manual', label: 'manual' },
                    { value: 'dynamic', label: 'dynamic' },
                    { value: 'bebridge', label: 'bebridge' },
                  ]}
                  // Still read-only — the option exists so a bebridge playlist renders
                  // its type instead of a blank select.
                  disabled={true}
                />
              </Form.Item>
              {/* Ad cue point spacing for this playlist's MRSS feeds. Kept out of the bebridge
                  gate on purpose: the field lives on every playlist and the work order feed
                  simply carries no cue points. */}
              <Form.Item
                name="cue_point_interval"
                label="Cue Point Interval (seconds)"
                rules={[
                  {
                    type: 'integer',
                    min: CUE_POINT_INTERVAL_MIN,
                    max: CUE_POINT_INTERVAL_MAX,
                    message: `Enter ${CUE_POINT_INTERVAL_MIN}–${CUE_POINT_INTERVAL_MAX} seconds, or leave it empty.`,
                  },
                ]}
                extra={
                  `Ad cue points are placed every N seconds in the MRSS feeds ` +
                  `(amagi / xumo / vimai / odk / zype), plus the start and the end of each ` +
                  `video. Leave empty to use the default ${CUE_POINT_INTERVAL_DEFAULT}s ` +
                  `(${CUE_POINT_INTERVAL_DEFAULT / 60} min). The work order feed has no cue points.`
                }
              >
                <InputNumber
                  min={CUE_POINT_INTERVAL_MIN}
                  max={CUE_POINT_INTERVAL_MAX}
                  precision={0}
                  style={{ width: '200px' }}
                  placeholder={`${CUE_POINT_INTERVAL_DEFAULT} (default)`}
                />
              </Form.Item>
              {/* Shorts order. Rendered only for bebridge playlists — the backend rejects a
                  preset on any other type with 400 / errors.preset, and for those types the
                  field is not registered, so Update never sends it. */}
              {isWorkOrderPlaylist && (
                <Form.Item
                  name="preset"
                  label="Shorts Order Preset"
                  extra="The single <odk:order> of this playlist's work order feed. Reusable across playlists."
                >
                  <PresetSelect />
                </Form.Item>
              )}
              <Form.Item name="created" label="Created">
                <Input readOnly />
              </Form.Item>
            </Form>
          </div>

          {isWorkOrderPlaylist && (
            <div style={{ marginTop: '30px' }}>
              <h3>Shorts Work Order Feed</h3>
              {!selectedPreset && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: '12px', marginBottom: '12px' }}
                  message="No preset linked — this feed goes out without an <odk:order>."
                  description={
                    'The vendor has no spec to generate against, so nothing gets made. ' +
                    'Pick a preset above and save.'
                  }
                />
              )}
              <div style={{ marginTop: '12px' }}>
                <Text strong style={{ marginRight: '10px' }}>
                  Work Order Feed URL:
                </Text>
                <Link href={workOrderFeedUrl} target="_blank">
                  {workOrderFeedUrl}
                </Link>
                <Button
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={handleCopyFeedUrl}
                  aria-label="Copy work order feed URL"
                />
                <Button type="link" onClick={() => setFeedModalOpen(true)}>
                  View feed
                </Button>
              </div>
              {/* Operators read "0 items" as a broken feed and file it as a bug; the spec
                  treats an empty window as a normal state (§7.2). */}
              <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                Public URL — the vendor polls it directly, no login. Rolling{' '}
                {WORK_ORDER_WINDOW_DAYS}-day window ({WORK_ORDER_MAX_ITEMS} items at most), not an
                archive: an empty feed is a normal state, not an error. Episodes come from this
                playlist&apos;s rules — set them per season, series, tag or recency on the{' '}
                <b>Playlist Rule</b> tab; a group that needs a different order gets its own
                playlist.
              </Text>
            </div>
          )}

          <div>
            <div className="playlist-sources">
              <h3>Playlist MRSS Sources</h3>
            </div>
            <div className="playlist-mrss" style={{ marginTop: '30px' }}>
              {MRSS_SERVICES.map(({ name, label }, index) => {
                const url = buildMrssUrl(String(id), name);
                return (
                  <React.Fragment key={name}>
                    {index > 0 && <br />}
                    <Text strong style={{ marginLeft: '30px', marginRight: '10px' }}>
                      {label} MRSS Feed URL:
                    </Text>
                    <Link href={url} target="_blank">
                      {url}
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="playlist-detail-update-button" style={{ marginTop: '30px' }}>
            <Component.ColorButtonComponent text={'Update'} onClick={handleUpdate} />
            <Button
              type="primary"
              loading={validating}
              onClick={handleValidateFeed}
              style={{ marginLeft: '10px', backgroundColor: '#1890ff' }}
            >
              Validate Feed
            </Button>
          </div>

          <Modal
            title="Feed Validation Result"
            open={modalOpen}
            onCancel={() => setModalOpen(false)}
            footer={null}
            width={700}
          >
            {validationResult && (
              <>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <Tag>Total: {validationResult.total_count}</Tag>
                  <Tag color="success">Valid: {validationResult.valid_count}</Tag>
                  <Tag color="error">Invalid: {validationResult.invalid_count}</Tag>
                </div>
                <Table
                  dataSource={validationResult.invalid_items}
                  columns={columns}
                  rowKey="content_id"
                  pagination={false}
                  size="small"
                  title={() => <Text strong>Invalid Items</Text>}
                />
                {recoverableCount >= 2 && (
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <Button
                      type="primary"
                      onClick={handleRecoverAll}
                      disabled={recoverAllProgress !== null}
                    >
                      {recoverAllProgress ?? 'Recover All'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </Modal>

          <WorkOrderFeedModal
            open={feedModalOpen}
            onClose={() => setFeedModalOpen(false)}
            playlistId={Number(id)}
            hasPreset={Boolean(selectedPreset)}
          />
        </div>
      )}
    </>
  );
};
